import {
  AchatsState,
  AchatsAction,
  AchatFormData,
  PurchaseSummary,
} from "../types";
import dayjs from "dayjs";

const toNumberWithDecimalSupport = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized =
    typeof value === "string"
      ? value.replace(/\s/g, "").replace(/,/g, ".")
      : value;
  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizePurchaseSummary = (summary?: any | null): PurchaseSummary => ({
  total_purchase_count: toNumberWithDecimalSupport(
    summary?.total_purchase_count ?? summary?.total_sales_count
  ),
  total_purchase_amount: toNumberWithDecimalSupport(
    summary?.total_purchase_amount ?? summary?.total_sales_amount
  ),
  paid_amount: toNumberWithDecimalSupport(summary?.paid_amount),
  unpaid_amount: toNumberWithDecimalSupport(summary?.unpaid_amount),
  partially_paid_amount: toNumberWithDecimalSupport(
    summary?.partially_paid_amount
  ),
  partially_due_amount: toNumberWithDecimalSupport(
    summary?.partially_due_amount ??
      Math.max(
        toNumberWithDecimalSupport(summary?.total_due_amount) -
          toNumberWithDecimalSupport(summary?.unpaid_amount),
        0
      )
  ),
  total_paid_amount: toNumberWithDecimalSupport(
    summary?.total_paid_amount ?? summary?.total_received_amount
  ),
  total_due_amount: toNumberWithDecimalSupport(summary?.total_due_amount),
});

const emptyPurchaseSummary = normalizePurchaseSummary(null);

// État initial pour le reducer
export const initialFormData: AchatFormData = {
  Date_Facture: dayjs().format("YYYY-MM-DD"),
  due_date: dayjs().format("YYYY-MM-DD"),
  Statut_Achat: "Commandé",
  produitsAches: [],
  remise_globale: 0,
};

export const initialState: AchatsState = {
  achats: [],
  fournisseurs: [],
  purchaseOrders: [],
  produits: [],
  taxes: [],
  loading: false,
  error: null,
  currentPage: 1,
  itemsPerPage: 10,
  totalPages: 1,
  searchTerm: "",
  dateRange: {
    start: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    end: dayjs().format("YYYY-MM-DD"),
  },
  selectedFournisseur: null,
  showForm: false,
  showScanner: false,
  formData: initialFormData,
  showDeleted: false,
  activeTab: "all",
  purchaseSummary: emptyPurchaseSummary,
};

// Reducer pour gérer les actions
export const achatsReducer = (
  state: AchatsState,
  action: AchatsAction
): AchatsState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ACHATS":
      return {
        ...state,
        achats: action.payload.achats,
        purchaseSummary: normalizePurchaseSummary(action.payload.summary),
        totalPages: Math.ceil(action.payload.total / state.itemsPerPage),
        loading: false,
        error: null,
      };
    case "DELETE_ACHAT":
      return {
        ...state,
        achats: state.achats.filter((achat) => achat.id !== action.payload),
      };
    case "SET_FOURNISSEURS":
      return { ...state, fournisseurs: action.payload, loading: false };
    case "SET_PURCHASE_ORDERS":
      return { ...state, purchaseOrders: action.payload, loading: false };
    case "SET_PRODUITS":
      return { ...state, produits: action.payload, loading: false };
    case "SET_TAXES":
      return { ...state, taxes: action.payload, loading: false };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "SET_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_ITEMS_PER_PAGE":
      return { ...state, itemsPerPage: action.payload, currentPage: 1 };
    case "SET_SEARCH":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "SET_DATE_RANGE":
      return { ...state, dateRange: action.payload, currentPage: 1 };
    case "SET_SELECTED_FOURNISSEUR":
      return { ...state, selectedFournisseur: action.payload, currentPage: 1 };
    case "TOGGLE_FORM":
      return { ...state, showForm: action.payload };
    case "TOGGLE_SCANNER":
      return { ...state, showScanner: action.payload };
    case "SET_FORM_DATA":
      return { ...state, formData: action.payload };
    case "UPDATE_FORM_DATA":
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
      };
    case "UPDATE_ACHAT_DETAIL":
      return {
        ...state,
        achats: state.achats.map((achat) =>
          achat.id === action.payload.id
            ? { ...achat, produitsAches: action.payload.produitsAches }
            : achat
        ),
      };
    case "TOGGLE_SHOW_DELETED":
      return { ...state, showDeleted: action.payload, currentPage: 1 };
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload };
    default:
      return state;
  }
};
